import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router";
import { Trash2 } from "lucide-react";
import { useToast } from "../../../components/ui/Toast";
import {
  GET_PRICING_PLAN,
  UPDATE_PRICING_PLAN,
  DELETE_PRICING_PLAN,
  ALL_PRICING_PLANS,
} from "./queries";
import type { PricingPlanRecord } from "./types";

export function PricingPlanEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const ppQuery = useQuery<{ PricingPlan: PricingPlanRecord }>(
    GET_PRICING_PLAN,
    { variables: { id }, skip: !id },
  );

  const [updatePlan, { loading: saving }] = useMutation(UPDATE_PRICING_PLAN);
  const [deletePlan, { loading: deleting }] = useMutation(DELETE_PRICING_PLAN);

  const plan = ppQuery.data?.PricingPlan;

  // Form state
  const [name, setName] = useState("");
  const [authAmount, setAuthAmount] = useState<number>(0);
  const [hourlyRate, setHourlyRate] = useState<number>(0);
  // Track which record's data is loaded so switching to another plan in
  // the rail re-populates the form.
  const [loadedId, setLoadedId] = useState<string | null>(null);

  useEffect(() => {
    if (plan && plan.id !== loadedId) {
      setName(plan.name);
      setAuthAmount(plan.authAmount);
      setHourlyRate(plan.hourlyRate);
      setLoadedId(plan.id);
    }
  }, [plan, loadedId]);

  const canSave = name.trim().length > 0 && !saving;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave || !id) return;

    try {
      await updatePlan({
        variables: {
          id,
          name: name.trim(),
          authAmount,
          hourlyRate,
        },
        refetchQueries: [{ query: ALL_PRICING_PLANS }],
      });
      toast.push({
        kind: "success",
        title: "Plan updated",
        description: `${name.trim()} has been saved.`,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      toast.push({ kind: "error", title: "Save failed", description: msg });
    }
  }

  async function handleDelete() {
    if (!id) return;
    const confirmed = window.confirm(
      `Delete "${name}"? Services referencing this plan will lose their tier assignments.`,
    );
    if (!confirmed) return;

    try {
      await deletePlan({
        variables: { id },
        refetchQueries: [{ query: ALL_PRICING_PLANS }],
      });
      toast.push({
        kind: "success",
        title: "Plan deleted",
        description: `${name} has been removed.`,
      });
      navigate("/settings/catalog");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Delete failed",
        description: msg,
      });
    }
  }

  if (ppQuery.loading && !ppQuery.data) {
    return (
      <div className="max-w-xl px-7 py-8 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-canvas-2 rounded pulse-soft" />
        ))}
      </div>
    );
  }

  if (ppQuery.error) {
    return (
      <div className="max-w-xl px-7 py-8">
        <div className="text-rust">
          Error loading plan: {ppQuery.error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <form onSubmit={handleSave} className="px-7 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="min-w-0">
            <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-1">
              Price plan
            </div>
            <h2 className="font-display text-[26px] font-medium tracking-tight truncate">
              {plan?.name ?? "Edit plan"}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="text-[11.5px] text-rust hover:text-rust/80 flex items-center gap-1 mt-2 flex-shrink-0"
          >
            <Trash2 size={12} strokeWidth={2} />
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Plan details
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Name *
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Authorization amount ($)
                </span>
                <input
                  type="number"
                  value={authAmount}
                  onChange={(e) =>
                    setAuthAmount(parseFloat(e.target.value) || 0)
                  }
                  min={0}
                  step={0.01}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>

              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Hourly rate ($)
                </span>
                <input
                  type="number"
                  value={hourlyRate}
                  onChange={(e) =>
                    setHourlyRate(parseFloat(e.target.value) || 0)
                  }
                  min={0}
                  step={0.01}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="hair-t border-t pt-5 flex items-center justify-end gap-2">
          <Link
            to="/settings/catalog"
            className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 hover:bg-paper-2"
          >
            Discard
          </Link>
          <button
            type="submit"
            disabled={!canSave}
            className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
